/**
 * Krok UXR2 — KARTA „NEMOVITOST" SE STITKY PUVODU (mockup `03-detail-zakazky.html`).
 *
 * Predloha u kazdeho udaje rika, ODKUD je: `katastr` / `klient` / `makléř` /
 * `nevyplněno`. Tenhle modul je cisty vypocet nad `CaseDetail` — zadny novy
 * dotaz na API a zadny novy zdroj pravdy o hodnotach: vsechny hodnoty jdou
 * pres EXISTUJICI selektory `@reos/reports` (`caseHeader`, `areaRows`,
 * `areaReconciliationResult`, `accessSummary`, `utilitiesSummary`), stejne
 * jako je dnes cte levy i pravy sloupec detailu.
 *
 * Co je nove: PUVOD. Odvozuje se z toho, co o faktu vime, ne z dohadu:
 *
 *   - `USER_INPUT` fakt            -> makléř (tvrzeni maklere, krok G4/U1),
 *   - dukaz z katastru/RUIAN       -> katastr,
 *   - dukaz `RAW_INPUT`/`ANSWER_*` -> klient (podklad, ktery dodal klient),
 *   - jiny verejny registr         -> verejny zdroj (uzemni plan, DIBAVOD,
 *                                     inzertni trh, web obce…),
 *   - zadna hodnota                -> nevyplněno.
 *
 * ROZHODNUTI UXR2-3: paty stitek „verejny zdroj" predloha nema, ale data ano
 * (`up.municipal`, `dibavod`, `market.*`, `municipal.web`, `water.operator`).
 * Oznacit je za „katastr" by byla lez a nechat radek bez stitku by vypadalo
 * jako chyba — stitek proto existuje, nese NEUTRALNI styl predlohy
 * (`tag--non`) a vlastni popisek.
 */

import { t } from '@reos/i18n';
/*
 * Krok P8F6 bod 3: popisek a zapis hodnoty faktu karty jsou TYTEZ, jake
 * kresli nabor (katalog `property.fact.*`) — zadny druhy prevod.
 */
import { fieldLabel, formatValue } from '../components/field-intake/values.js';
import { humanizeConnectionValue } from './case-captions.js';
import {
  accessSummary,
  areaReconciliationResult,
  areaRows,
  caseHeader,
  utilitiesSummary,
  type CaseDetail,
  type R3FixtureFact,
} from './dd-api.js';

const cs = { locale: 'cs' } as const;

/** Stitky predlohy + `public` (viz ROZHODNUTI UXR2-3). */
export type FactOrigin = 'cadastre' | 'client' | 'broker' | 'public' | 'none';

/** Trida stitku podle predlohy (`.tag--kat` / `--kli` / `--mak` / `--non`). */
export const ORIGIN_TAG_CLASS: Readonly<Record<FactOrigin, string>> = {
  cadastre: 'rh-a2-tag--kat',
  client: 'rh-a2-tag--kli',
  broker: 'rh-a2-tag--mak',
  public: 'rh-a2-tag--non',
  none: 'rh-a2-tag--non',
};

export function originCaption(origin: FactOrigin): string {
  return t(`webAdmin.caseOrigin.${origin}` as never, cs);
}

/**
 * Adaptery katastru a RUIAN. Vycet je zamerne vycet, ne regulerni vyraz pres
 * „obsahuje cuzk": `cuzk.ortofoto.wms` je mapovy podklad, ne evidence udaju.
 */
const CADASTRE_ADAPTER_PREFIXES: readonly string[] = [
  'ruian.',
  'cuzk.inspire',
  'cuzk.nahlizeni',
  'cuzk.dp',
  'wsdp',
  'nahlizeni',
];

/** Dukazy, ktere do pripadu vlozil clovek s podkladem od klienta. */
const CLIENT_ADAPTERS: readonly string[] = ['RAW_INPUT', 'ANSWER_ATTACHMENT'];

function originOfEvidence(adapter: string): FactOrigin {
  if (CLIENT_ADAPTERS.includes(adapter)) return 'client';
  if (CADASTRE_ADAPTER_PREFIXES.some((prefix) => adapter.startsWith(prefix))) return 'cadastre';
  // `human.<source>` je rucni opis, ktery porizoval makler (krok K0e).
  if (adapter.startsWith('human.')) return 'broker';
  return 'public';
}

/**
 * Puvod jednoho faktu. Poradi je podstatne: `USER_INPUT` vyhrava nad
 * evidenci, protoze tvrzeni maklere se zapisuje BEZ dukazu a pripadna
 * evidence na stejnem klici patri predchozi (prekonane) hodnote.
 */
export function factOrigin(detail: CaseDetail, fact: R3FixtureFact | null): FactOrigin {
  if (fact === null) return 'none';
  if (fact.status === 'USER_INPUT') return 'broker';
  const origins = fact.evidenceIds
    .map((id) => detail.evidenceById.get(id))
    .filter((evidence): evidence is NonNullable<typeof evidence> => evidence !== undefined)
    .map((evidence) => originOfEvidence(evidence.sourceAdapter));
  if (origins.length === 0) return 'none';
  // Nejpresnejsi puvod vyhrava — katastr pred podkladem klienta pred zbytkem.
  for (const preferred of ['cadastre', 'client', 'broker'] as const) {
    if (origins.includes(preferred)) return preferred;
  }
  return 'public';
}

/** Fakt podle klice (presne, nebo prvni s danym prefixem `klic#…`). */
function factFor(detail: CaseDetail, key: string): R3FixtureFact | null {
  return (
    detail.facts.find((fact) => fact.factKey === key) ??
    detail.facts.find((fact) => fact.factKey.startsWith(`${key}#`)) ??
    null
  );
}

export interface PropertyFactRow {
  readonly key: string;
  readonly label: string;
  readonly value: string | null;
  readonly origin: FactOrigin;
}

/** Stejny format jako zbytek detailu (`zakazka-detail.tsx`): „2 807 m²". */
function m2(value: number): string {
  return `${value.toLocaleString('cs-CZ')} m²`;
}

function landTypeOf(detail: CaseDetail): string | null {
  const fact = detail.facts.find((f) => f.factKey.startsWith('parcel.identity#'));
  const value = fact?.value;
  if (value === null || typeof value !== 'object') return null;
  const landType = (value as Record<string, unknown>)['landType'];
  return typeof landType === 'string' && landType !== '' ? landType : null;
}

function priceOf(detail: CaseDetail): string | null {
  const { low, high } = detail.fixture.valuationBand;
  if (low === null || high === null) return null;
  const format = (amount: string, currency: string): string =>
    new Intl.NumberFormat('cs-CZ', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(amount));
  return low.amount === high.amount
    ? format(low.amount, low.currency)
    : `${format(low.amount, low.currency)} – ${format(high.amount, high.currency)}`;
}

/**
 * Radky karty „Nemovitost" v poradi predlohy. Radek s `value === null` se
 * NESKRYVA — „zatim nevime" je informace, kterou makler potrebuje videt
 * (predloha ma u „Přípojky" prave stitek `nevyplněno`).
 */
export function propertyFactRows(detail: CaseDetail): readonly PropertyFactRow[] {
  const header = caseHeader(detail);
  const rows = areaRows(detail);
  const area = areaReconciliationResult(detail);
  const access = accessSummary(detail);
  const utilities = utilitiesSummary(detail);
  const parcelFact = detail.facts.find((f) => f.factKey.startsWith('parcel.label#')) ?? null;
  const areaFact = detail.facts.find((f) => f.factKey.startsWith('parcel.area.kn_m2#')) ?? null;
  /*
   * Hodnota pripojky z RUIAN je „pripoj na vodovodni sit (kod 1)" — strojovy
   * kod na obrazovku maklere nepatri (krok CZ1). Prevod uz existuje a pouziva
   * ho zalozka Site, takze karta nema vlastni. Ke kazdemu stavu patri i to,
   * ceho se tyka: „připojeno · nezjištěno" samo o sobe nerika, co je co.
   */
  const connections = (
    [
      [t('webAdmin.caseDetail.utilities.water', cs), utilities.water],
      [t('webAdmin.caseDetail.utilities.sewer', cs), utilities.sewer],
      [t('webAdmin.caseDetail.utilities.gas', cs), utilities.gas],
    ] as const
  )
    .filter((entry): entry is readonly [string, string] => entry[1] !== null)
    .map(([label, value]) => `${label}: ${humanizeConnectionValue(value).text}`);

  const spec: readonly PropertyFactRow[] = [
    {
      key: 'cadastralArea',
      label: t('webAdmin.caseDetail.left.cadastralArea', cs),
      value: header.cadastralAreaName,
      origin: factOrigin(detail, factFor(detail, 'scope.parcels') ?? parcelFact),
    },
    {
      key: 'parcels',
      label: t('webAdmin.caseDetail.propertyCard.parcels', cs),
      value: rows.length > 0 ? rows.map((row) => row.parcelLabel).join(', ') : null,
      origin: factOrigin(detail, parcelFact),
    },
    {
      key: 'areaKn',
      label: t('webAdmin.caseDetail.propertyCard.areaKn', cs),
      value: area.knCandidateTotalM2 !== null ? m2(area.knCandidateTotalM2) : null,
      origin: factOrigin(detail, areaFact),
    },
    {
      key: 'landType',
      label: t('webAdmin.caseDetail.propertyCard.landType', cs),
      value: landTypeOf(detail),
      origin: factOrigin(detail, parcelFact),
    },
    {
      key: 'confirmedScope',
      label: t('webAdmin.caseDetail.propertyCard.confirmedScope', cs),
      value: area.confirmedSubjectTotalM2 !== null ? m2(area.confirmedSubjectTotalM2) : null,
      origin: factOrigin(detail, factFor(detail, 'scope.parcels')),
    },
    {
      key: 'frontage',
      label: t('webAdmin.caseDetail.propertyCard.frontage', cs),
      value: access.frontageM !== null ? `${String(access.frontageM)} m` : null,
      origin: factOrigin(detail, factFor(detail, 'access.street_frontage')),
    },
    {
      key: 'connections',
      label: t('webAdmin.caseDetail.propertyCard.connections', cs),
      value: connections.length > 0 ? connections.join(' · ') : null,
      origin: factOrigin(detail, factFor(detail, 'utilities.house_connections_ruian')),
    },
    {
      /*
       * Krok PROP3 bod 1 — TOHLE JE ODHAD CENY, NE NABIDKOVA CENA.
       *
       * Hodnota je cenove pasmo z provereni (`fixture.valuationBand`). Dokud
       * se radek jmenoval „Cena", cetl ho makler jako cenu, pod kterou se
       * nemovitost nabizi — a ta zije JINDE (fakt `asking_price`, odkud ji
       * bere inzerat a verejna stranka). Odhad se do nabidkove ceny nikdy
       * nezapisuje (audit propojeni 14. 10., nalez 12).
       */
      key: 'price',
      label: t('webAdmin.caseDetail.propertyCard.priceEstimate', cs),
      value: priceOf(detail),
      origin: priceOf(detail) === null ? 'none' : 'public',
    },
  ];

  // Chybejici hodnota ma vzdy stitek „nevyplněno" — i kdyz fakt existuje,
  // ale je prazdny (jinak by radek tvrdil „katastr" u nicoho).
  return spec.map((row) => (row.value === null ? { ...row, origin: 'none' as const } : row));
}

/**
 * Krok P8F6 bod 3 — PUVOD FAKTU KARTY NEMOVITOSTI (`property_facts`).
 *
 * Fakta karty nesou `provenance` (status + sourceType), ne `evidenceIds` jako
 * fakta provereni vyse — proto vlastni prevod na TYTEZ ctyri stitky predlohy.
 *
 * ODLISNOST OD SSOT, ZDUVODNENI (CLAUDE.md §8/2): tutez uvahu dela
 * `originOfProvenance` v `@reos/listing` (puvod pole konceptu inzeratu).
 * Zavolat ji odsud nejde — `web-admin` ten balicek v zavislostech NEMA
 * (hranice kontextu, eslint `boundaries`) a cilovy vycet je jiny: inzerat
 * rozlisuje `penb`/`document`/`listing`, karta zakazky ma stitky predlohy
 * `katastr / klient / makléř / veřejný zdroj`. Shodne je rozhodujici
 * pravidlo: `user` + neoverene = makler, `registry` = katastr.
 */
export function provenanceOrigin(
  provenance: { readonly status: string; readonly sourceType: string } | undefined,
): FactOrigin {
  if (provenance === undefined) return 'none';
  if (provenance.status === 'VERIFIED' || provenance.sourceType === 'registry') return 'cadastre';
  switch (provenance.sourceType) {
    case 'user':
      return 'broker';
    // Tvrzeni majitele i podklad od nej = „klient" (stitek predlohy).
    case 'owner':
    case 'document':
      return 'client';
    default:
      return 'public';
  }
}

/** Fakta karty, ktera detail zakazky ukazuje — v poradi, v jakem je cte makler. */
export const CASE_PROPERTY_FACT_KEYS: readonly string[] = [
  'asking_price',
  'sale.owner_price_expectation',
  'usable_area_m2',
  'disposition',
  'floor_number',
  'unit_number',
  /*
   * Krok ZAK1b bod 2 — PARCELA A POLOHA. Pozemek mel na detailu zakazky
   * z karty nula radku (cena a dispozice u nej nejsou), prestoze parcelu,
   * uzemni plan a bonitu uz predvyplneni z otevrenych dat zna. U bytu a domu
   * je `parcels` POZEMEK DOMU, ne predmet — popisek „Parcely" to rika a
   * druh pozemku („zastavena plocha a nadvori") to u stavebni parcely
   * potvrzuje.
   */
  /*
   * Krok ZAK1b: rok vystavby, podlazi a vytapeni uz predvyplneni z RUIAN zna
   * od ZAK1, ale karta zakazky je NEKRESLILA — makler je videl az v naboru.
   */
  'build_year',
  'floors_count',
  'heating_type',
  'parcels',
  'cadastral_territory',
  'zoning_function_area',
  'zoning_plan_name',
  'terrain_slope_deg',
  'bpej_protection_class',
  /*
   * Krok APP3 bod 6 (nalez A13) — MAPOVE VRSTVY NA KARTE.
   *
   * Radon, zaplavove uzemi a ochranna pasma uz zakazka zna hned po zalozeni
   * (predvyplneni z otevrenych dat je od APP3 nacita bez behu provereni),
   * ale karta je NEKRESLILA — makléřka je nasla az v reportu. Jsou to
   * jednohodnotove udaje polohy, takze patri do teze mrizky jako svazitost
   * a trida ochrany ZPF.
   */
  'radon_index',
  'flood_zone',
  'protection_zones',
  'utilities_available',
];

/**
 * Radky bloku „Údaje nemovitosti" z KARTY nemovitosti (krok P8F6 bod 3).
 *
 * Nalez P8-N11: po naboru detail zakazky ukazoval jen „1 parcela · 803 m²" —
 * cena, plocha, dispozice, patro ani jednotka tam nebyly, prestoze na karte
 * jsou. Hodnota se formatuje TYMZ `formatValue`, jakym ji kresli nabor
 * (`components/field-intake/values.ts`), popisek je katalogovy klic
 * `property.fact.*`; druhy prevod hodnot tu nevznika.
 *
 * Radek bez hodnoty se NEKRESLI: prazdny blok u zakazky bez faktu by jen
 * zabiral misto (na rozdil od karty „Nemovitost", kde „nevyplněno" u parcel
 * je informace z predlohy).
 */
export function casePropertyFactRows(
  facts: readonly {
    readonly factKey: string;
    readonly value: unknown;
    readonly provenance?: { readonly status: string; readonly sourceType: string } | undefined;
  }[],
): readonly PropertyFactRow[] {
  const out: PropertyFactRow[] = [];
  for (const key of CASE_PROPERTY_FACT_KEYS) {
    // Nabidkova cena vyhrava nad predstavou majitele — druhy radek s cenou
    // by makleere nutil hadat, ktera plati (PROP3 bod 3).
    if (key === 'sale.owner_price_expectation' && out.some((row) => row.key === 'asking_price')) continue;
    const fact = facts.find((item) => item.factKey === key);
    if (fact === undefined || fact.value === null || fact.value === undefined || fact.value === '') continue;
    out.push({
      key,
      label: fieldLabel(key),
      value: formatValue(key, fact.value),
      origin: provenanceOrigin(fact.provenance),
    });
  }
  return out;
}

/** Legenda pod kartou — jen stitky, ktere se na teto zakazce opravdu objevily. */
export function originLegend(rows: readonly PropertyFactRow[]): readonly FactOrigin[] {
  const order: readonly FactOrigin[] = ['cadastre', 'client', 'broker', 'public', 'none'];
  const used = new Set(rows.map((row) => row.origin));
  return order.filter((origin) => used.has(origin));
}
